import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import RankBadge from '../elo/RankBadge';

/**
 * Inline-editable team name.
 *
 * Renders as a normal h3 by default. When the leader clicks it, the heading
 * swaps for a text input pre-filled with the current name. Enter or blur saves
 * via the provided callback; Escape cancels and restores the prior value.
 *
 * Non-leaders see read-only text.
 *
 * @param {string} name - Current display name.
 * @param {boolean} editable - Whether the leader-edit affordance is enabled.
 * @param {(name: string) => void} onSave - Called with the new trimmed value.
 * @param {string} className - Passed through to wrapper for team-1/team-2 styling.
 */
function EditableTeamName({ name, editable, onSave, className }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(name); }, [name]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const next = (draft || '').trim();
    if (next && next !== name) {
      onSave(next);
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className={`team-name-input ${className || ''}`}
        value={draft}
        maxLength={32}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
      />
    );
  }

  return (
    <h3
      className={`team-name ${className || ''} ${editable ? 'editable' : ''}`}
      onClick={editable ? () => setEditing(true) : undefined}
      title={editable ? t('createMatch.clickToRename') : undefined}
    >
      {name}
    </h3>
  );
}

/**
 * Teams display step with shuffle, move-player, and inline team-name edit.
 */
export default function StepTeams({
  match, isQueueMatch, isLeader, leaderName, loading,
  onShuffle, onMovePlayer, onTeamNameChange, onBack, onNext,
}) {
  const { t } = useTranslation();
  const teamAvgElo = (team) => {
    const elos = team?.players?.map(p => p.current_elo?.elo).filter(Boolean) || [];
    return elos.length ? Math.round(elos.reduce((a, b) => a + b, 0) / elos.length) : null;
  };

  const t1Avg = teamAvgElo(match.team1);
  const t2Avg = teamAvgElo(match.team2);

  const team1DisplayName = match.team1_name || match.team1?.name || 'Team 1';
  const team2DisplayName = match.team2_name || match.team2?.name || 'Team 2';

  return (
    <div className="step-content">
      {isQueueMatch && (
        <div className="lobby-status">
          <div className="lobby-pulse" />
          {isLeader ? (
            <span>{t('createMatch.leaderHint')}</span>
          ) : (
            <span>{t('createMatch.waitingForLeader', { name: leaderName })}</span>
          )}
        </div>
      )}

      <h2>{t('createMatch.teams')}</h2>
      <div className="teams-container">
        <div className="team-column">
          <EditableTeamName
            name={team1DisplayName}
            editable={isLeader && !loading && !!onTeamNameChange}
            onSave={(name) => onTeamNameChange(1, name)}
            className="team1"
          />
          {t1Avg && <div className="team-avg-elo">Snitt: {t1Avg} ELO</div>}
          {match.team1?.players?.map(p => (
            <div key={p.id} className="team-player">
              {p.steam_user?.avatar && <img src={p.steam_user.avatar} alt="" className="team-player-avatar" />}
              <span className="team-player-name">{p.steam_user?.username || p.discord_user?.username}</span>
              {p.current_elo && (
                <span className="team-player-elo">
                  <RankBadge rank={p.current_elo.rank} size="small" />
                  {p.current_elo.elo}
                </span>
              )}
              {isLeader && (
                <button
                  className="move-player-btn move-right"
                  onClick={() => onMovePlayer(p.id, 2)}
                  disabled={loading || (match.team1?.players?.length || 0) <= 1}
                  title={t('createMatch.moveToOtherTeam')}
                >&#8594;</button>
              )}
            </div>
          ))}
        </div>
        <div className="teams-vs">{t('createMatch.vs')}</div>
        <div className="team-column">
          <EditableTeamName
            name={team2DisplayName}
            editable={isLeader && !loading && !!onTeamNameChange}
            onSave={(name) => onTeamNameChange(2, name)}
            className="team2"
          />
          {t2Avg && <div className="team-avg-elo">Snitt: {t2Avg} ELO</div>}
          {match.team2?.players?.map(p => (
            <div key={p.id} className="team-player">
              {isLeader && (
                <button
                  className="move-player-btn move-left"
                  onClick={() => onMovePlayer(p.id, 1)}
                  disabled={loading || (match.team2?.players?.length || 0) <= 1}
                  title={t('createMatch.moveToOtherTeam')}
                >&#8592;</button>
              )}
              {p.steam_user?.avatar && <img src={p.steam_user.avatar} alt="" className="team-player-avatar" />}
              <span className="team-player-name">{p.steam_user?.username || p.discord_user?.username}</span>
              {p.current_elo && (
                <span className="team-player-elo">
                  <RankBadge rank={p.current_elo.rank} size="small" />
                  {p.current_elo.elo}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {isLeader && (
        <>
          <div className="team-actions">
            <button className="shuffle-btn" onClick={() => onShuffle('balanced')} disabled={loading}>
              Balanced Shuffle
            </button>
            <button className="shuffle-btn random" onClick={() => onShuffle('random')} disabled={loading}>
              Random Shuffle
            </button>
          </div>

          <div className="step-nav">
            {!isQueueMatch && (
              <button className="back-btn" onClick={onBack}>{t('profile.back')}</button>
            )}
            <button className="next-btn" onClick={onNext}>
              {isQueueMatch ? t('createMatch.pickMaps') : t('createMatch.nextMapPool')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
